import {useSnackbar} from '@/contexts/snackbar';
import {Box} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import DialogForm from '../ui/dialogs/form-dialog';
import EventForm from '../forms/events';
import {EventFormMode} from '@/types/event';
import {checkVerification} from '@/services/payments';
import {useAppSelector} from '@/redux-store/store';
import {getPaymentFee} from '@/rest/payments/get-payment-fee';
import {setPaymentFeeData} from '@/redux-store/slices/auth';
import {useDispatch} from 'react-redux';

interface OnboardingRedirectProps {
  mode: EventFormMode;
  eventId?: string;
}

const OnboardingRedirect: React.FC<OnboardingRedirectProps> = ({
  mode,
  eventId
}) => {
  const {t} = useTranslation();

  const [showCreateForm, setShowCreateForm] = useState(true);
  const {stripeAccountVerified, paymentFeeData} = useAppSelector(
    (state) => state.auth
  );

  const {showSnackbarMessage} = useSnackbar();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCloseCreateForm = useCallback(() => {
    setShowCreateForm(false);
    navigate('/');
  }, [navigate]);

  const handleEventCreationFormSubmitted = useCallback(() => {
    handleCloseCreateForm();
    showSnackbarMessage(t('event_create_success'), 'success');
    navigate('/');
  }, [handleCloseCreateForm, navigate, showSnackbarMessage, t]);

  const onEventCreationError = useCallback(() => {
    showSnackbarMessage(t('event_create_failure'), 'error');
    navigate('/');
  }, [navigate, showSnackbarMessage, t]);

  const handleEventUpdateFormSubmitted = useCallback(() => {
    handleCloseCreateForm();
    showSnackbarMessage(t('event_update_success'), 'success');
    navigate('/');
  }, [handleCloseCreateForm, navigate, showSnackbarMessage, t]);

  const onEventUpdateError = useCallback(() => {
    showSnackbarMessage(t('event_update_failure'), 'error');
    navigate('/');
  }, [navigate, showSnackbarMessage, t]);

  useEffect(() => {
    const checkAndFetchFeeData = async () => {
      try {
        const isVerified = await checkVerification();
        if (isVerified && !paymentFeeData) {
          const paymentFeeData = await getPaymentFee();
          if (paymentFeeData) {
            dispatch(setPaymentFeeData(paymentFeeData));
          } else {
            if (mode === 'update') {
              onEventUpdateError();
            } else {
              onEventCreationError();
            }
          }
        }
      } catch (error) {
        console.error(
          'Error during verification or fetching payment fee data:',
          error
        );
        if (mode === 'update') {
          onEventUpdateError();
        } else {
          onEventCreationError();
        }
      }
    };

    checkAndFetchFeeData();
  }, [
    dispatch,
    mode,
    onEventCreationError,
    onEventUpdateError,
    paymentFeeData
  ]);

  return (
    <Box>
      <DialogForm
        open={showCreateForm}
        onSubmit={
          mode === 'update'
            ? handleEventUpdateFormSubmitted
            : handleEventCreationFormSubmitted
        }
        onCancel={handleCloseCreateForm}
        onError={mode === 'update' ? onEventUpdateError : onEventCreationError}
        FormComponent={EventForm}
        eventId={eventId}
        mode={mode}
        isVerified={stripeAccountVerified}
        maxHeightAllowed={'1200px'}
      />
    </Box>
  );
};

export default OnboardingRedirect;
